import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building, Plus, Pencil, Trash2, Save, X, Loader2, Shield,
  Users, Building2, CheckCircle2, XCircle, Copy, Key, Mail, User,
  Settings2, Palette, KeyRound, Globe, Upload, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TenantManagement = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null);
  const [ssoTenant, setSsoTenant] = useState(null);
  const [brandTenant, setBrandTenant] = useState(null);
  const [form, setForm] = useState({
    name: '', code: '', status: 'active',
    company_name: '', admin_name: '', admin_email: '', admin_password: '',
  });

  useEffect(() => {
    if (user?.role === 'super_admin') fetchTenants();
  }, [user]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/tenants`, { withCredentials: true });
      const enriched = await Promise.all(
        data.map(async (t) => {
          try {
            const detail = await axios.get(`${API_URL}/tenants/${t.id}`, { withCredentials: true });
            return { ...t, user_count: detail.data.user_count, company_count: detail.data.company_count };
          } catch { return { ...t, user_count: 0, company_count: 0 }; }
        })
      );
      setTenants(enriched);
    } catch (err) { toast.error('Failed to fetch tenants'); }
    finally { setLoading(false); }
  };

  const resetForm = () => ({
    name: '', code: '', status: 'active',
    company_name: '', admin_name: '', admin_email: '', admin_password: '',
  });

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghkmnpqrstuvwxyz23456789';
    const specials = '@#$!&';
    let pw = '';
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    pw += specials[Math.floor(Math.random() * specials.length)];
    pw += Math.floor(Math.random() * 100);
    return pw;
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Tenant name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API_URL}/tenants/${editingId}`, { name: form.name, status: form.status }, { withCredentials: true });
        toast.success('Tenant updated');
        setShowForm(false);
        setEditingId(null);
        setForm(resetForm());
      } else {
        // Validate provisioning fields
        if (!form.company_name.trim()) { toast.error('Default company name is required'); setSaving(false); return; }
        if (!form.admin_email.trim()) { toast.error('Admin email is required'); setSaving(false); return; }
        if (!form.admin_password.trim()) { toast.error('Admin password is required'); setSaving(false); return; }

        const { data } = await axios.post(`${API_URL}/tenants`, form, { withCredentials: true });
        toast.success('Tenant created with company & admin!');
        setShowForm(false);
        setForm(resetForm());
        // Show credentials popup
        if (data.provisioned_admin) {
          setShowCredentials({
            tenant_name: data.name,
            tenant_code: data.code,
            company: data.provisioned_company,
            admin: data.provisioned_admin,
          });
        }
      }
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({ ...resetForm(), name: t.name, code: t.code || '', status: t.status || 'active' });
    setShowForm(true);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Deactivate tenant "${t.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/tenants/${t.id}`, { withCredentials: true });
      toast.success('Tenant deactivated');
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (user?.role !== 'super_admin') {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Access denied. Super Admin only.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="tenant-management-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Tenant Management</h1>
            <p className="mt-1 text-sm text-slate-500">Create and manage tenants. Each tenant gets its own company and admin login.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full font-semibold flex items-center gap-1">
              <Shield className="w-3 h-3" /> Super Admin Only
            </span>
            <Button data-testid="add-tenant-btn"
              onClick={() => { setShowForm(true); setEditingId(null); setForm({ ...resetForm(), admin_password: generatePassword() }); }}
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Add Tenant
            </Button>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white border-2 border-blue-200 rounded-xl p-6 space-y-5 shadow-sm" data-testid="tenant-form">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-base text-slate-900 flex items-center gap-2">
                {editingId ? <Pencil className="w-5 h-5 text-blue-600" /> : <Plus className="w-5 h-5 text-blue-600" />}
                {editingId ? 'Edit Tenant' : 'Create New Tenant'}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tenant Info */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tenant Details</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Tenant Name *</label>
                  <input data-testid="tenant-name-input" value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      if (editingId) {
                        // Edit mode — only update name. Code is immutable; company name
                        // is irrelevant (already provisioned on create).
                        setForm({ ...form, name });
                      } else {
                        // Create mode — auto-derive code + suggest company name.
                        const code = name.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 8);
                        setForm({ ...form, name, code, admin_email: form.admin_email || '', company_name: form.company_name || `${name} Pvt Ltd` });
                      }
                    }}
                    placeholder="e.g. Horizon Industries"
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Tenant Code
                    {editingId && <span className="ml-1 text-[10px] font-normal text-slate-400">(read-only)</span>}
                  </label>
                  <input data-testid="tenant-code-input" value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    disabled={!!editingId}
                    title={editingId ? 'Tenant code is immutable — used by SSO entity IDs, SCIM, audit trail.' : ''}
                    placeholder="AUTO"
                    className={`w-full border-2 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#0052FF] ${editingId ? 'bg-slate-100 border-slate-200 cursor-not-allowed text-slate-500' : 'border-slate-200'}`} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select data-testid="tenant-status-select" value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Auto-Provisioning (only for new tenants) */}
            {!editingId && (
              <>
                <div className="border-t border-slate-200 pt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> Default Company
                  </p>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Company Name *</label>
                    <input data-testid="tenant-company-input" value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      placeholder="e.g. Horizon Industries Pvt Ltd"
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5" /> Tenant Admin Credentials
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Admin Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input data-testid="tenant-admin-name" value={form.admin_name}
                          onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                          placeholder="Auto: [Tenant] Admin"
                          className="w-full border-2 border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Admin Email *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input data-testid="tenant-admin-email" type="email" value={form.admin_email}
                          onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                          placeholder="admin@tenant.com"
                          className="w-full border-2 border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Password *</label>
                      <div className="relative">
                        <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input data-testid="tenant-admin-password" value={form.admin_password}
                          onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                          className="w-full border-2 border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#0052FF]" />
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">The admin user will be able to login immediately and start configuring their tenant.</p>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
              <Button data-testid="tenant-save-btn" onClick={handleSave} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white px-6">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingId ? 'Update' : 'Create Tenant'}
              </Button>
            </div>
          </div>
        )}

        {/* Credentials Popup */}
        {showCredentials && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="credentials-modal">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
              <div className="p-6 text-center border-b bg-green-50 rounded-t-xl">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h2 className="text-xl font-bold text-slate-900">Tenant Created Successfully!</h2>
                <p className="text-sm text-slate-600 mt-1">Share these credentials with the tenant admin</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Tenant</p>
                  <p className="text-sm font-semibold text-slate-900">{showCredentials.tenant_name}</p>
                  <p className="text-xs text-slate-500 font-mono">{showCredentials.tenant_code}</p>
                </div>
                {showCredentials.company && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Default Company</p>
                    <p className="text-sm font-semibold text-slate-900">{showCredentials.company.name}</p>
                  </div>
                )}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <p className="text-[10px] font-semibold text-blue-600 uppercase mb-3">Admin Login Credentials</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-blue-100">
                      <div>
                        <p className="text-[10px] text-slate-400">Email</p>
                        <p className="text-sm font-mono font-medium text-slate-900">{showCredentials.admin.email}</p>
                      </div>
                      <button onClick={() => copyToClipboard(showCredentials.admin.email)} className="text-blue-500 hover:text-blue-700 p-1"><Copy className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-blue-100">
                      <div>
                        <p className="text-[10px] text-slate-400">Password</p>
                        <p className="text-sm font-mono font-medium text-slate-900">{showCredentials.admin.password}</p>
                      </div>
                      <button onClick={() => copyToClipboard(showCredentials.admin.password)} className="text-blue-500 hover:text-blue-700 p-1"><Copy className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <button onClick={() => copyToClipboard(`Email: ${showCredentials.admin.email}\nPassword: ${showCredentials.admin.password}`)}
                    data-testid="copy-all-credentials"
                    className="w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium">
                    <Copy className="w-3.5 h-3.5" /> Copy All Credentials
                  </button>
                </div>
              </div>
              <div className="p-4 border-t bg-slate-50 rounded-b-xl text-center">
                <Button data-testid="close-credentials-btn" onClick={() => setShowCredentials(null)} className="px-8">
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total Tenants</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">{tenants.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Active</p>
            <p className="text-2xl font-semibold text-green-600 mt-1 font-mono">{tenants.filter(t => t.status === 'active').length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Inactive</p>
            <p className="text-2xl font-semibold text-red-600 mt-1 font-mono">{tenants.filter(t => t.status !== 'active').length}</p>
          </div>
        </div>

        {/* Tenants Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="tenants-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Code</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Users</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Companies</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Created</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-slate-500">
                  <Building className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No tenants found</p>
                </td></tr>
              ) : tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">{t.name}</td>
                  <td className="py-3 px-4 text-xs font-mono text-slate-500">{t.code || '—'}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {t.status === 'active' ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {t.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-center"><span className="inline-flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5" /> {t.user_count || 0}</span></td>
                  <td className="py-3 px-4 text-sm text-center"><span className="inline-flex items-center gap-1 text-slate-600"><Building2 className="w-3.5 h-3.5" /> {t.company_count || 0}</span></td>
                  <td className="py-3 px-4 text-xs text-slate-500 font-mono">{t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`tenant-sso-${t.id}`} onClick={() => setSsoTenant(t)} className="p-1.5 bg-indigo-50 hover:bg-indigo-100 rounded text-indigo-600" title="SSO / SCIM"><Settings2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`tenant-brand-${t.id}`} onClick={() => setBrandTenant(t)} className="p-1.5 bg-purple-50 hover:bg-purple-100 rounded text-purple-600" title="Branding"><Palette className="w-3.5 h-3.5" /></button>
                      <button data-testid={`tenant-edit-${t.id}`} onClick={() => handleEdit(t)} className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded text-slate-600" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`tenant-delete-${t.id}`} onClick={() => handleDelete(t)} className="p-1.5 bg-red-50 hover:bg-red-100 rounded text-red-600" title="Deactivate"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* SSO / SCIM Modal */}
        {ssoTenant && (
          <SSOConfigModal
            tenant={ssoTenant}
            onClose={() => setSsoTenant(null)}
            apiUrl={API_URL}
          />
        )}

        {/* Branding Modal */}
        {brandTenant && (
          <BrandingModal
            tenant={brandTenant}
            onClose={() => setBrandTenant(null)}
            apiUrl={API_URL}
          />
        )}
      </div>
    </MainLayout>
  );
};

// ===========================================================================
// SSO / SCIM CONFIG MODAL
// ===========================================================================
const SSOConfigModal = ({ tenant, onClose, apiUrl }) => {
  const [tab, setTab] = useState('saml');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [scimToken, setScimToken] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [metadataXml, setMetadataXml] = useState('');
  const [metadataUrl, setMetadataUrl] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/tenants/${tenant.id}/sso-config`, { withCredentials: true });
        setCfg(data);
      } catch (e) { toast.error('Failed to load SSO config'); }
      finally { setLoading(false); }
    })();
  }, [tenant.id, apiUrl]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (tab === 'saml') payload.saml = cfg.saml;
      else if (tab === 'oidc') payload.oidc = cfg.oidc;
      await axios.put(`${apiUrl}/tenants/${tenant.id}/sso-config`, payload, { withCredentials: true });
      toast.success('SSO config saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const parseMetadata = async () => {
    if (!metadataUrl && !metadataXml) { toast.error('Provide URL or XML'); return; }
    try {
      const { data } = await axios.post(
        `${apiUrl}/tenants/${tenant.id}/sso/saml/parse-metadata`,
        { metadata_url: metadataUrl || null, metadata_xml: metadataXml || null },
        { withCredentials: true }
      );
      setCfg({ ...cfg, saml: { ...cfg.saml, ...data, enabled: true } });
      toast.success('IdP metadata parsed');
      setMetadataUrl(''); setMetadataXml('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to parse metadata'); }
  };

  const regenerateScim = async () => {
    if (!window.confirm('This will invalidate the current SCIM token. Continue?')) return;
    try {
      const { data } = await axios.post(`${apiUrl}/tenants/${tenant.id}/scim/regenerate-token`, {}, { withCredentials: true });
      setScimToken(data.scim_token);
      setCfg((c) => ({ ...c, scim: { ...c.scim, has_token: true, endpoint: data.endpoint } }));
      toast.success('SCIM token rotated — copy it now');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const baseScimUrl = `${process.env.REACT_APP_BACKEND_URL}/api/scim/v2`;
  const acsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/sso/saml/${tenant.id}/acs`;
  const spEntity = `${process.env.REACT_APP_BACKEND_URL}/api/sso/saml/${tenant.id}/metadata`;
  const spMetadataUrl = spEntity;
  const oidcCallback = `${process.env.REACT_APP_BACKEND_URL}/api/sso/oidc/${tenant.id}/callback`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="sso-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Settings2 className="w-5 h-5 text-indigo-600" /> Enterprise SSO &amp; SCIM</h2>
            <p className="text-xs text-slate-500 mt-0.5">{tenant.name} — {tenant.code}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="border-b bg-slate-50">
          {['saml', 'oidc', 'scim'].map((k) => (
            <button key={k} data-testid={`sso-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-5 py-3 text-sm font-medium uppercase tracking-wider ${tab === k ? 'text-indigo-700 border-b-2 border-indigo-600 bg-white' : 'text-slate-500'}`}>
              {k === 'saml' ? 'SAML 2.0' : k === 'oidc' ? 'OpenID Connect' : 'SCIM 2.0'}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          {loading || !cfg ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : tab === 'saml' ? (
            <>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <input data-testid="saml-enabled" type="checkbox" checked={cfg.saml.enabled}
                  onChange={(e) => setCfg({ ...cfg, saml: { ...cfg.saml, enabled: e.target.checked } })} />
                Enable SAML 2.0 single sign-on for this tenant
              </label>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs space-y-1">
                <p className="font-semibold text-blue-900 mb-1">Service Provider details — share these with your IdP admin:</p>
                <div className="flex items-center justify-between"><span className="text-slate-600">SP Entity ID:</span> <code className="font-mono text-[11px]">{spEntity}</code> <button onClick={() => copy(spEntity)}><Copy className="w-3 h-3" /></button></div>
                <div className="flex items-center justify-between"><span className="text-slate-600">ACS URL:</span> <code className="font-mono text-[11px]">{acsUrl}</code> <button onClick={() => copy(acsUrl)}><Copy className="w-3 h-3" /></button></div>
                <div className="flex items-center justify-between"><span className="text-slate-600">Metadata XML:</span> <a href={spMetadataUrl} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-blue-700 underline">{spMetadataUrl}</a></div>
              </div>

              <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quick setup — paste IdP metadata</p>
                <input data-testid="saml-md-url" value={metadataUrl} onChange={(e) => setMetadataUrl(e.target.value)} placeholder="Metadata URL (e.g. https://samltest.id/saml/idp)" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" />
                <textarea data-testid="saml-md-xml" value={metadataXml} onChange={(e) => setMetadataXml(e.target.value)} placeholder="...or paste raw IdP metadata XML here" rows={4} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-xs font-mono" />
                <Button data-testid="saml-md-parse" onClick={parseMetadata} variant="outline" size="sm"><Upload className="w-3.5 h-3.5 mr-1" /> Parse &amp; pre-fill</Button>
              </div>

              {[
                ['idp_entity_id', 'IdP Entity ID'],
                ['idp_sso_url', 'IdP SSO URL'],
                ['idp_slo_url', 'IdP SLO URL (optional)'],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
                  <input data-testid={`saml-${k}`} value={cfg.saml[k] || ''}
                    onChange={(e) => setCfg({ ...cfg, saml: { ...cfg.saml, [k]: e.target.value } })}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">IdP X.509 Certificate (PEM body)</label>
                <textarea data-testid="saml-cert" value={cfg.saml.idp_x509_cert || ''}
                  onChange={(e) => setCfg({ ...cfg, saml: { ...cfg.saml, idp_x509_cert: e.target.value } })}
                  rows={5} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-xs font-mono" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs font-semibold text-slate-700 mb-1">Email attribute</label><input data-testid="saml-attr-email" value={cfg.saml.attr_email} onChange={(e) => setCfg({ ...cfg, saml: { ...cfg.saml, attr_email: e.target.value } })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-semibold text-slate-700 mb-1">First-name attribute</label><input value={cfg.saml.attr_first_name} onChange={(e) => setCfg({ ...cfg, saml: { ...cfg.saml, attr_first_name: e.target.value } })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-semibold text-slate-700 mb-1">Last-name attribute</label><input value={cfg.saml.attr_last_name} onChange={(e) => setCfg({ ...cfg, saml: { ...cfg.saml, attr_last_name: e.target.value } })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Default JIT role</label>
                <select data-testid="saml-default-role" value={cfg.saml.default_role} onChange={(e) => setCfg({ ...cfg, saml: { ...cfg.saml, default_role: e.target.value } })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option value="employee">Employee</option><option value="hr_admin">HR Admin</option><option value="payroll_admin">Payroll Admin</option><option value="department_manager">Department Manager</option><option value="tenant_admin">Tenant Admin</option>
                </select>
              </div>
            </>
          ) : tab === 'oidc' ? (
            <>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <input data-testid="oidc-enabled" type="checkbox" checked={cfg.oidc.enabled} onChange={(e) => setCfg({ ...cfg, oidc: { ...cfg.oidc, enabled: e.target.checked } })} />
                Enable OpenID Connect single sign-on for this tenant
              </label>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                <p className="font-semibold text-blue-900 mb-1">Redirect URI — register this with your OIDC provider:</p>
                <div className="flex items-center justify-between"><code className="font-mono text-[11px] break-all">{oidcCallback}</code> <button onClick={() => copy(oidcCallback)}><Copy className="w-3 h-3" /></button></div>
              </div>
              {[
                ['issuer', 'Issuer / Discovery URL', 'https://accounts.google.com'],
                ['client_id', 'Client ID', ''],
                ['client_secret', 'Client Secret', '********'],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
                  <input data-testid={`oidc-${k}`} value={cfg.oidc[k] || ''} placeholder={ph}
                    onChange={(e) => setCfg({ ...cfg, oidc: { ...cfg.oidc, [k]: e.target.value } })}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Scopes (comma separated)</label>
                <input data-testid="oidc-scopes" value={(cfg.oidc.scopes || []).join(', ')}
                  onChange={(e) => setCfg({ ...cfg, oidc: { ...cfg.oidc, scopes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Default JIT role</label>
                <select data-testid="oidc-default-role" value={cfg.oidc.default_role} onChange={(e) => setCfg({ ...cfg, oidc: { ...cfg.oidc, default_role: e.target.value } })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option value="employee">Employee</option><option value="hr_admin">HR Admin</option><option value="payroll_admin">Payroll Admin</option><option value="department_manager">Department Manager</option><option value="tenant_admin">Tenant Admin</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-700">SCIM 2.0 lets your IdP automatically create, update, and deactivate users in this tenant.</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-2">
                <div className="flex items-center justify-between"><span className="text-slate-600">SCIM Base URL:</span> <code className="font-mono text-[11px]">{baseScimUrl}</code> <button onClick={() => copy(baseScimUrl)}><Copy className="w-3 h-3" /></button></div>
                <div className="flex items-center justify-between"><span className="text-slate-600">Auth method:</span> <span className="font-mono text-[11px]">OAuth Bearer Token</span></div>
              </div>

              {scimToken ? (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-semibold text-amber-900 uppercase">New SCIM Token (copy now — won't be shown again):</p>
                  <div className="bg-white border border-amber-200 rounded p-2 flex items-center justify-between gap-2">
                    <code className="font-mono text-[11px] break-all flex-1">{showSecret ? scimToken : '•'.repeat(48)}</code>
                    <button onClick={() => setShowSecret(!showSecret)} className="text-slate-500">{showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                    <button onClick={() => copy(scimToken)} className="text-blue-600"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-600">
                  {cfg.scim?.has_token ? 'A SCIM token already exists. Regenerating will invalidate it.' : 'No SCIM token issued yet.'}
                </div>
              )}

              <Button data-testid="scim-regen-btn" onClick={regenerateScim} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <KeyRound className="w-4 h-4 mr-2" /> {cfg.scim?.has_token ? 'Rotate SCIM token' : 'Generate SCIM token'}
              </Button>
            </>
          )}
        </div>

        {tab !== 'scim' && (
          <div className="px-6 py-4 border-t bg-slate-50 flex justify-end gap-2 sticky bottom-0">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="sso-save-btn" onClick={save} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// BRANDING / THEME MODAL
// ===========================================================================
const BrandingModal = ({ tenant, onClose, apiUrl }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [b, setB] = useState(null);
  const [original, setOriginal] = useState(null);
  const dirty = !!(b && original && JSON.stringify(b) !== JSON.stringify(original));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/tenants/${tenant.id}/branding`, { withCredentials: true });
        setB(data);
        setOriginal(data);
      } catch { toast.error('Failed to load branding'); }
      finally { setLoading(false); }
    })();
  }, [tenant.id, apiUrl]);

  const requestClose = () => {
    if (dirty && !window.confirm('You have unsaved branding changes that will be LOST. Close anyway?')) return;
    onClose();
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/tenants/${tenant.id}/branding`, b, { withCredentials: true });
      toast.success('Branding saved — tenant users will see the new theme on their next page load');
      setOriginal(b);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading || !b) return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>
  );

  const fields = [
    ['app_name', 'App Name', 'text'],
    ['login_title', 'Login Title (override)', 'text'],
    ['login_subtitle', 'Login Subtitle', 'text'],
    ['logo_url', 'Logo URL (full https://...)', 'text'],
    ['favicon_url', 'Favicon URL', 'text'],
    ['font_family', 'Font Family (CSS name)', 'text'],
    ['email_header', 'Email Header HTML', 'textarea'],
    ['email_footer', 'Email Footer HTML', 'textarea'],
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="branding-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Palette className="w-5 h-5 text-purple-600" /> Tenant Branding &amp; Theme</h2>
            <p className="text-xs text-slate-500 mt-0.5">{tenant.name} — drives login page, in-app colors, fonts, and email templates</p>
          </div>
          <button onClick={requestClose} data-testid="brand-close-btn" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {dirty && (
          <div className="mx-5 mt-4 px-4 py-2 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-2 text-xs text-amber-900" data-testid="brand-dirty-banner">
            <span className="font-semibold">⚠ Unsaved changes</span>
            <span className="opacity-80">— click <strong>Save Branding</strong> at the bottom to persist. Closing without saving will discard your edits.</span>
          </div>
        )}

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            {[
              ['primary_color', 'Primary'],
              ['secondary_color', 'Secondary'],
              ['accent_color', 'Accent'],
              ['email_primary_color', 'Email Primary'],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
                <div className="flex items-center gap-2">
                  <input data-testid={`brand-${k}`} type="color" value={b[k] || '#000000'} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="w-10 h-10 border border-slate-200 rounded cursor-pointer" />
                  <input value={b[k] || ''} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="flex-1 border-2 border-slate-200 rounded-lg px-3 py-2 text-xs font-mono" />
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Layout Density</label>
            <select data-testid="brand-density" value={b.layout_density || 'comfortable'} onChange={(e) => setB({ ...b, layout_density: e.target.value })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
            </select>
          </div>

          {fields.map(([k, label, type]) => (
            <div key={k}>
              <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
              {type === 'textarea' ? (
                <textarea data-testid={`brand-${k}`} value={b[k] || ''} onChange={(e) => setB({ ...b, [k]: e.target.value })} rows={3} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-xs font-mono" />
              ) : (
                <input data-testid={`brand-${k}`} value={b[k] || ''} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" />
              )}
            </div>
          ))}

          {/* Live preview */}
          <div className="border-2 border-slate-200 rounded-xl p-5" style={{ background: '#FFFFFF' }}>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Live Preview</p>
            <div className="flex items-center gap-3 mb-3">
              {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-10 w-10 rounded object-contain" /> : <div className="h-10 w-10 rounded" style={{ background: b.primary_color }} />}
              <div>
                <h3 className="text-lg font-bold" style={{ color: b.secondary_color, fontFamily: b.font_family }}>{b.app_name || 'Payroll'}</h3>
                <p className="text-xs text-slate-500">{b.login_subtitle || 'Welcome back. Sign in to continue.'}</p>
              </div>
            </div>
            <button className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: b.primary_color }}>Sign in</button>
            <button className="ml-2 px-4 py-2 rounded-lg text-sm font-semibold border-2" style={{ borderColor: b.accent_color, color: b.accent_color }}>SSO Login</button>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between gap-2 sticky bottom-0">
          <div className="text-xs">
            {dirty ? (
              <span className="text-amber-700 font-semibold">● Unsaved changes — click Save Branding →</span>
            ) : (
              <span className="text-slate-400">No unsaved changes</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={requestClose}>Cancel</Button>
            <Button data-testid="brand-save-btn" onClick={save} disabled={saving || !dirty}
              className={`text-white ${dirty ? 'bg-purple-600 hover:bg-purple-700 animate-pulse-once shadow-lg shadow-purple-200 ring-2 ring-purple-300' : 'bg-slate-300 cursor-not-allowed'}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Branding
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantManagement;
